import React, { useState, useEffect } from 'react';
import { Card, CardHeader, CardTitle, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';
import { Users, UserPlus, Shield, Edit, Trash2, Copy, Check, KeyRound } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

const UserManagement = () => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showInvite, setShowInvite] = useState(false);
  const [inviteData, setInviteData] = useState({ email: '', name: '', role: 'editor' });
  const [newUserCredentials, setNewUserCredentials] = useState(null);
  const [copiedPassword, setCopiedPassword] = useState(false);
  const [currentUser, setCurrentUser] = useState(null);
  
  const token = localStorage.getItem('token');

  useEffect(() => {
    fetchUsers();
    fetchCurrentUser();
  }, []);

  const fetchCurrentUser = async () => {
    try {
      const res = await fetch(`${API}/api/auth/me`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setCurrentUser(data);
      }
    } catch (err) {
    }
  };

  const fetchUsers = async () => {
    try {
      const res = await fetch(`${API}/api/users`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setUsers(data);
      } else if (res.status === 403) {
        toast.error('Admin access required');
      }
    } catch (err) {
      toast.error('Failed to fetch users');
    } finally {
      setLoading(false);
    }
  };

  const handleInvite = async (e) => {
    e.preventDefault();
    try {
      const res = await fetch(`${API}/api/users/invite`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(inviteData)
      });
      
      const data = await res.json();
      
      if (res.ok) {
        toast.success(`User ${inviteData.name} created!`);
        setNewUserCredentials({
          email: inviteData.email,
          password: data.temporary_password
        });
        setInviteData({ email: '', name: '', role: 'editor' });
        fetchUsers();
      } else {
        toast.error(data.detail || 'Failed to invite user');
      }
    } catch (err) {
      toast.error('Failed to invite user');
    }
  };

  const updateUserRole = async (userId, newRole) => {
    try {
      const res = await fetch(`${API}/api/users/${userId}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ role: newRole })
      });
      
      if (res.ok) {
        toast.success('Role updated');
        fetchUsers();
      } else {
        const data = await res.json();
        toast.error(data.detail || 'Failed to update role');
      }
    } catch (err) {
      toast.error('Failed to update role');
    }
  };

  const deleteUser = async (userId, userName) => {
    if (!window.confirm(`Are you sure you want to delete ${userName}?`)) return;
    
    try {
      const res = await fetch(`${API}/api/users/${userId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (res.ok) {
        toast.success('User deleted');
        fetchUsers();
      } else {
        const data = await res.json();
        toast.error(data.detail || 'Failed to delete user');
      }
    } catch (err) {
      toast.error('Failed to delete user');
    }
  };

  const resetPassword = async (userId, userName) => {
    const newPass = window.prompt(`Enter new password for ${userName} (min 6 chars):`);
    if (!newPass || newPass.length < 6) {
      if (newPass) toast.error('Password must be at least 6 characters');
      return;
    }
    try {
      const res = await fetch(`${API}/api/admin/reset-password/${userId}`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ new_password: newPass })
      });
      if (res.ok) {
        toast.success(`Password reset for ${userName}. 2FA also disabled — they'll need to set it up again.`);
      } else {
        const data = await res.json();
        toast.error(data.detail || 'Failed to reset password');
      }
    } catch (err) {
      toast.error('Failed to reset password');
    }
  };



  const copyPassword = () => {
    navigator.clipboard.writeText(newUserCredentials.password);
    setCopiedPassword(true);
    setTimeout(() => setCopiedPassword(false), 2000);
  };

  const getRoleBadge = (role) => {
    const roleValue = role || 'viewer';
    const colors = {
      admin: 'bg-red-500/20 text-red-300 border-red-500/30',
      editor: 'bg-blue-500/20 text-blue-300 border-blue-500/30',
      viewer: 'bg-gray-500/20 text-gray-300 border-gray-500/30'
    };
    return (
      <Badge className={`${colors[roleValue] || colors.viewer} border`}>
        {roleValue.charAt(0).toUpperCase() + roleValue.slice(1)}
      </Badge>
    );
  };

  if (currentUser?.role !== 'admin') {
    return (
      <div className="min-h-screen bg-gray-950 text-white p-8">
        <Card className="bg-gray-900 border-gray-800 max-w-md mx-auto mt-20">
          <CardContent className="p-8 text-center">
            <Shield className="w-16 h-16 text-red-500 mx-auto mb-4" />
            <h2 className="text-xl font-bold mb-2">Admin Access Required</h2>
            <p className="text-gray-400">Only administrators can manage users.</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-950 text-white p-8">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div className="flex items-center gap-3">
            <Users className="w-8 h-8 text-purple-400" />
            <h1 className="text-2xl font-bold">Team Management</h1>
          </div>
          <Button 
            onClick={() => setShowInvite(!showInvite)}
            className="bg-purple-600 hover:bg-purple-700"
          >
            <UserPlus className="w-4 h-4 mr-2" />
            Invite User
          </Button>
        </div>

        {/* New User Credentials Card */}
        {newUserCredentials && (
          <Card className="bg-green-900/20 border-green-500/30 mb-6">
            <CardContent className="p-4">
              <div className="flex items-start justify-between">
                <div>
                  <h3 className="font-semibold text-green-300 mb-2">✅ User Created Successfully!</h3>
                  <p className="text-sm text-gray-300 mb-1">
                    <strong>Email:</strong> {newUserCredentials.email}
                  </p>
                  <p className="text-sm text-gray-300 flex items-center gap-2">
                    <strong>Temporary Password:</strong> 
                    <code className="bg-gray-800 px-2 py-1 rounded">{newUserCredentials.password}</code>
                    <Button size="sm" variant="ghost" onClick={copyPassword}>
                      {copiedPassword ? <Check className="w-4 h-4 text-green-400" /> : <Copy className="w-4 h-4" />}
                    </Button>
                  </p>
                  <p className="text-xs text-yellow-400 mt-2">
                    ⚠️ Share this password securely. User will need to change it on first login.
                  </p>
                </div>
                <Button 
                  size="sm" 
                  variant="ghost" 
                  onClick={() => setNewUserCredentials(null)}
                >
                  ✕
                </Button>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Invite Form */}
        {showInvite && (
          <Card className="bg-gray-900 border-gray-800 mb-6">
            <CardHeader>
              <CardTitle className="text-lg">Invite New Team Member</CardTitle>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleInvite} className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>Name</Label>
                    <Input
                      value={inviteData.name}
                      onChange={(e) => setInviteData({...inviteData, name: e.target.value})}
                      placeholder="John Doe"
                      className="bg-gray-800 border-gray-700"
                      required
                    />
                  </div>
                  <div>
                    <Label>Email</Label>
                    <Input
                      type="email"
                      value={inviteData.email}
                      onChange={(e) => setInviteData({...inviteData, email: e.target.value})}
                      placeholder="john@company.com"
                      className="bg-gray-800 border-gray-700"
                      required
                    />
                  </div>
                </div>
                <div>
                  <Label>Role</Label>
                  <div className="flex gap-3 mt-2">
                    {['admin', 'editor', 'viewer'].map((role) => (
                      <Button
                        key={role}
                        type="button"
                        variant={inviteData.role === role ? 'default' : 'outline'}
                        onClick={() => setInviteData({...inviteData, role})}
                        className={inviteData.role === role ? 'bg-purple-600' : 'border-gray-700'}
                      >
                        {role.charAt(0).toUpperCase() + role.slice(1)}
                      </Button>
                    ))}
                  </div>
                  <p className="text-xs text-gray-500 mt-2">
                    {inviteData.role === 'admin' && '🔴 Admin: Full access to all features and user management'}
                    {inviteData.role === 'editor' && '🔵 Editor: Can create and manage their own channels'}
                    {inviteData.role === 'viewer' && '⚪ Viewer: View-only access to dashboard'}
                  </p>
                </div>
                <div className="flex gap-2 justify-end">
                  <Button type="button" variant="ghost" onClick={() => setShowInvite(false)}>
                    Cancel
                  </Button>
                  <Button type="submit" className="bg-purple-600 hover:bg-purple-700">
                    Send Invite
                  </Button>
                </div>
              </form>
            </CardContent>
          </Card>
        )}

        {/* Users List */}
        <Card className="bg-gray-900 border-gray-800">
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <Users className="w-5 h-5" />
              Team Members ({users.length})
            </CardTitle>
          </CardHeader>
          <CardContent>
            {loading ? (
              <p className="text-gray-400">Loading...</p>
            ) : (
              <div className="space-y-3">
                {users.map((user) => (
                  <div 
                    key={user.id} 
                    className="flex items-center justify-between p-4 bg-gray-800/50 rounded-lg border border-gray-700"
                  >
                    <div className="flex items-center gap-4">
                      <div className="w-10 h-10 rounded-full bg-purple-600/30 flex items-center justify-center">
                        {user.name?.charAt(0)?.toUpperCase() || 'U'}
                      </div>
                      <div>
                        <p className="font-medium">{user.name}</p>
                        <p className="text-sm text-gray-400">{user.email}</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-3">
                      {getRoleBadge(user.role)}
                      {user.id !== currentUser?.id && (
                        <div className="flex gap-1">
                          <select
                            value={user.role}
                            onChange={(e) => updateUserRole(user.id, e.target.value)}
                            className="bg-gray-700 border border-gray-600 rounded px-2 py-1 text-sm"
                          >
                            <option value="viewer">Viewer</option>
                            <option value="editor">Editor</option>
                            <option value="admin">Admin</option>
                          </select>
                          <Button 
                            size="sm" 
                            variant="ghost" 
                            className="text-yellow-400 hover:text-yellow-300"
                            onClick={() => resetPassword(user.id, user.name)}
                            data-testid={`reset-pwd-${user.id}`}
                            title="Reset Password"
                          >
                            <KeyRound className="w-4 h-4" />
                          </Button>
                          <Button 
                            size="sm" 
                            variant="ghost" 
                            className="text-red-400 hover:text-red-300"
                            onClick={() => deleteUser(user.id, user.name)}
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      )}
                      {user.id === currentUser?.id && (
                        <span className="text-xs text-gray-500">(You)</span>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Role Explanation */}
        <Card className="bg-gray-900/50 border-gray-800 mt-6">
          <CardContent className="p-4">
            <h3 className="font-semibold mb-3">Role Permissions</h3>
            <div className="grid grid-cols-3 gap-4 text-sm">
              <div>
                <div className="flex items-center gap-2 mb-2">
                  {getRoleBadge('admin')}
                </div>
                <ul className="text-gray-400 space-y-1">
                  <li>✓ Manage all users</li>
                  <li>✓ View/edit all channels</li>
                  <li>✓ Full system access</li>
                </ul>
              </div>
              <div>
                <div className="flex items-center gap-2 mb-2">
                  {getRoleBadge('editor')}
                </div>
                <ul className="text-gray-400 space-y-1">
                  <li>✓ Create new channels</li>
                  <li>✓ Edit own channels</li>
                  <li>✓ Manage own schedules</li>
                </ul>
              </div>
              <div>
                <div className="flex items-center gap-2 mb-2">
                  {getRoleBadge('viewer')}
                </div>
                <ul className="text-gray-400 space-y-1">
                  <li>✓ View dashboard</li>
                  <li>✓ View all channels</li>
                  <li>✗ Cannot make changes</li>
                </ul>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default UserManagement;
